import { assign, reduce, find } from 'lodash';
import { EntityBase } from './EntityBase';

export const METRIC_CODES = {
  ASKING_RENT: 'MPF-HIST-ASK-RENT',
  ASKING_RPSF: 'MPF-HIST-AVG-ASK-RPSF',
  EFFECTIVE_RENT: 'MPF-RENT',
  EFFECTIVE_RPSF: 'MPF-RPSF',
  CONCESSIONS: 'MPF-HIST-CONC-RATIO',
  OCCUPANCY: 'MPF-OCC',
  ANNUAL_RENT_CHANGE: 'MPF-ANN-RENT-CHG',
  SUPPLY: 'MPF-ANN-COMPLETIONS',
  ABSORPTION: 'MPF-ANN-ABSORP',
  UNIT_COUNT: 'MPF-EU'
};

export const REGIONS = {
  MARKET: 'market',
  SUBMARKET: 'submarket',
  PROPERTY: 'property'
};

export interface MetricJSON {
  metric: string;
  timeSlot: string;
  unformatted: number;
  formatted: string;
}

export interface UnitTypeDataJSON {
  name: string;
  unitType: string;
  data: MetricJSON[];
}

export interface MetricInfoJSON {
  metricCode: string;
  metricValue: number;
  metricValueFormatted: string;
}

export interface MetricDataJSON { // UnitTypeMetric
  metricCode: string;
  unitTypeData: UnitTypeDataJSON[];
}

export interface RegionDataJSON {
  regionType: string;
  name: string;
  metricData: MetricDataJSON[];
}

export interface RentMetric {
  rent: number;
  rentPerSquareFoot: number;
}

export class Metrics extends EntityBase {
  regions: RegionDataJSON[];

  constructor(json: any) {
    super(json);
    assign(this, json);
    this.regions = json.regions;
  }
}

export class AercoMetric {
  asking: RentMetric;
  effective: RentMetric;
  concessions: number;
  occupancy: number;
  rentChange: number;

  constructor(json: any) {
    if (!json) return this;

    this.asking = { 'rent': json.asking ? json.asking.rent : json.hist.ask.rent, 'rentPerSquareFoot': json.asking ? json.asking.rentPerSquareFoot : json.hist.avg.ask.rentPerSquareFoot };
    this.effective = { 'rent': json.effective ? json.effective.rent : json.rent, 'rentPerSquareFoot': json.effective ? json.effective.rentPerSquareFoot : json.rentPerSquareFoot };
    this.concessions = json.hist && json.hist.conc.ratio;
    this.occupancy = json.occ;
    this.rentChange = json.ann && json.ann.rent.chg;
  }

  static fromJSON(json: MetricJSON[]) {
    const metrics = reduce(json || [], (a, b) => {
      return defineTree(a, b.unformatted, b.metric.toLowerCase().split('-').slice(1).map(replaceRPSF));
    }, {});
    return new AercoMetric(metrics);
  }
}

export class StudentAercoMetric {
  asking: RentMetric;
  effective: RentMetric;
  concessions: number;
  occupancy: number;
  rentChange: number;
  bedCount: number;

  constructor(json: any) {
    if (!json) return this;
    this.asking = { 'rent': json.hist && json.hist.ask.rent.pb, 'rentPerSquareFoot': json.hist && json.hist.avg.ask.rentPerSquareFoot.pb };
    this.effective = { 'rent': json.hist && json.hist.rent.pb, 'rentPerSquareFoot': json.hist && json.hist.rentPerSquareFoot.pb };
    this.concessions = json.hist && json.hist.conc.ratio.pb;
    this.occupancy = json.hist.occupancy;
    this.rentChange = json.hist && json.hist.ann.rent.pb.chg;
    this.bedCount = json.hist && json.hist.beds.sampled;
  }

  static fromJSON(json: MetricJSON[]) {
    const metrics = reduce(json || [], (a, b) => {
      return defineTree(a, b.unformatted, b.metric.toLowerCase().split('-').slice(1).map(replaceRPSF));
    }, {});
    return new StudentAercoMetric(metrics);
  }
}

export class MetricInfo {
  askingRent: number;
  askingRPSF: number;
  effectiveRent: number;
  effectiveRPSF: number;
  concessions: number;
  occupancy: number;
  annualRentChange: number;

  constructor(metricData?: any[]) {
    if (metricData && metricData.length) {
      this.askingRent = findMetric(metricData, METRIC_CODES.ASKING_RENT);
      this.askingRPSF = findMetric(metricData, METRIC_CODES.ASKING_RPSF);

      this.effectiveRent = findMetric(metricData, METRIC_CODES.EFFECTIVE_RENT);
      this.effectiveRPSF = findMetric(metricData, METRIC_CODES.EFFECTIVE_RPSF);

      this.concessions = findMetric(metricData, METRIC_CODES.CONCESSIONS);
      this.occupancy = findMetric(metricData, METRIC_CODES.OCCUPANCY);
    }
  }
}

const replaceRPSF = (value: string) => value && value.length && value === 'rpsf' ? 'rentPerSquareFoot' : value;

const defineTree = (object: object, value: any, args: string[]) => {
  if (!args || !args.length) return value;

  const last = args.length - 1;
  reduce(args, (a: object, b: string, idx: number) => {
    if (idx === last) {
      a[b] = value;
      return a;
    } else {
      a[b] = a[b] || {};
      return a[b];
    }
  }, object);

  return object;
}

export class MarketMetrics extends MetricInfo {
  annualRentChange: number;
  supply: number;
  absorption: number;
  unitCount: number;

  constructor(metricData?: MetricInfoJSON[]) {
    super(metricData);
    if (metricData && metricData.length) {
      this.annualRentChange = findMetric(metricData, METRIC_CODES.ANNUAL_RENT_CHANGE);
      this.supply = findMetric(metricData, METRIC_CODES.SUPPLY);
      this.absorption = findMetric(metricData, METRIC_CODES.ABSORPTION);
      this.unitCount = findMetric(metricData, METRIC_CODES.UNIT_COUNT);
    }
  }
}

function findMetric(metrics: MetricInfoJSON[], metricName: string): number {
  const metric = find(metrics, (m => m.metricCode === metricName));
  return metric ? metric.metricValue : null;
}



// WEBPACK FOOTER //
// ./src/app/core/model/Metrics.ts